// src/services/sessionRestore.ts
import { supabase } from "../../lib/supabase";
import { secureStorage } from "../Storage/secureStorage";
import { authInterceptor } from "./authInterceptor";
import { logger } from "../../utils/logger";

class SessionRestore {
  private isRestoring = false;

  async restoreSession(): Promise<boolean> {
    // Prevent duplicate restore attempts
    if (this.isRestoring) {
      // Suppress log - this is expected behavior
      return false;
    }

    this.isRestoring = true;
    
    try {
      // Mock credentials mode - restore from stored tokens without Supabase
      const { config } = await import("../../config/env");
      if (config.dev.mockCredentials) {
        const token = authInterceptor.getAccessToken();
        const storedUser = secureStorage.getUserData();
        
        if (token && storedUser) {
          // Verify it's a mock token (JWT-like format with 3 parts)
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            try {
              const payload = JSON.parse(atob(tokenParts[1]));
              if (payload.sub && payload.email) {
                console.log('🔓 Mock credentials mode: Session restored from stored tokens');
                this.isRestoring = false;
                return true;
              }
            } catch (e) {
              // Not a valid mock token format
            }
          }
        }
        this.isRestoring = false;
        return false;
      }
      
      // First, check if Supabase has a valid session (source of truth)
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (session?.refresh_token && session?.access_token) {
        // Supabase has a valid session - use it and sync to our storage
        if (process.env.NODE_ENV === 'development') {
          logger.log("Using Supabase session for restoration");
        }
        secureStorage.saveTokens(session.access_token, session.refresh_token);
        secureStorage.saveUserData(session.user);
        return true;
      }
      
      // Fallback: Only use stored tokens if Supabase has no session
      const refreshToken = secureStorage.getRefreshToken();
      if (!refreshToken) {
        // Suppress log - this is expected when user is not logged in
        return false;
      }
      
      // Attempt to refresh tokens using stored refresh token
      const newTokens = await authInterceptor.performRefresh();
      if (newTokens) {
        secureStorage.saveUserData(newTokens.user);
        // Only log successful restoration in development
        if (process.env.NODE_ENV === 'development') {
          logger.log("Session restored successfully");
        }
        return true;
      }

      // Suppress log - invalid/expired tokens are expected
      // Clear invalid tokens silently
      secureStorage.clearTokens();
      return false;
    } catch (error: any) {
      // Only log unexpected errors (not 400 Bad Request which is expected for invalid tokens)
      const isExpectedError = 
        error?.code === 'REFRESH_FAILED' || 
        error?.message?.includes('400') ||
        error?.message?.includes('Bad Request') ||
        error?.message?.includes('refresh');
      
      if (!isExpectedError) {
        // Only log unexpected errors
        console.error("Unexpected session restoration error:", error);
      }
      
      // Clear tokens on any error
      secureStorage.clearTokens();
      return false;
    } finally {
      this.isRestoring = false;
    }
  }

  async initializeApp() {
    const sessionRestored = await this.restoreSession();

    // Only log successful session restoration, suppress "without session" log
    // (it's expected when user is not logged in)
    if (process.env.NODE_ENV === 'development' && sessionRestored) {
      logger.log("App initialized with restored session");
    }

    return sessionRestored;
  }
}

export const sessionRestore = new SessionRestore();
